import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, CheckCheck, Check, Bell, Filter, ChevronLeft, ChevronRight, MailOpen, UserPlus, FileCheck2, FileX2, FileText, Sparkles, MailCheck, MousePointerClick, MailWarning } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNotifications } from "@/context/NotificationsContext";

const TYPE_LABELS = {
  quote_opened: "Quote Opened",
  quote_clicked: "Quote Clicked",
  quote_delivered: "Email Delivered",
  email_bounced: "Email Bounced",
  quote_accepted: "Quote Accepted",
  quote_rejected: "Quote Declined",
  quote_revision: "Revision Requested",
  new_customer: "New Customer",
  test: "Test",
};

const typeIcon = (t) => {
  switch (t) {
    case "quote_opened": return MailOpen;
    case "quote_accepted": return FileCheck2;
    case "quote_rejected": return FileX2;
    case "quote_revision": return FileText;
    case "quote_clicked": return MousePointerClick;
    case "quote_delivered": return MailCheck;
    case "email_bounced": return MailWarning;
    case "new_customer": return UserPlus;
    case "test": return Sparkles;
    default: return Bell;
  }
};

const typeTone = (t) => {
  switch (t) {
    case "quote_opened": return { bg: "#eff6ff", fg: "#1d4ed8" };
    case "quote_accepted": return { bg: "#ecfdf5", fg: "#047857" };
    case "quote_rejected": return { bg: "#fef2f2", fg: "#b91c1c" };
    case "quote_revision": return { bg: "#fff7ed", fg: "#c2410c" };
    case "quote_clicked": return { bg: "#e0f2fe", fg: "#0369a1" };
    case "quote_delivered": return { bg: "#ecfeff", fg: "#0e7490" };
    case "email_bounced": return { bg: "#fef2f2", fg: "#b91c1c" };
    case "new_customer": return { bg: "#f5f3ff", fg: "#6d28d9" };
    case "test": return { bg: "#fef9c3", fg: "#854d0e" };
    default: return { bg: "#f1f5f9", fg: "#334155" };
  }
};

export default function Notifications() {
  const nav = useNavigate();
  const { markRead: ctxMarkRead, markAllRead: ctxMarkAllRead, refresh } = useNotifications();
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [readFilter, setReadFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const params = useMemo(() => {
    const p = { skip: page * limit, limit };
    if (q.trim()) p.q = q.trim();
    if (type !== "all") p.type = type;
    if (readFilter !== "all") p.is_read = readFilter === "read";
    return p;
  }, [q, type, readFilter, page, limit]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setUnread(data.unread || 0);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params]);

  // Debounced search
  const [qLive, setQLive] = useState("");
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setQ(qLive); }, 300);
    return () => clearTimeout(t);
  }, [qLive]);

  const markOne = async (id) => {
    await ctxMarkRead(id);
    setRows((xs) => xs.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
  };

  const markAll = async () => {
    await ctxMarkAllRead();
    setRows((xs) => xs.map((n) => ({ ...n, is_read: true })));
    setUnread(0);
  };

  const open = async (n) => {
    if (!n.is_read) await markOne(n.id);
    if (n.quote_id) nav(`/dashboard/quotes/${n.quote_id}`);
    else if (n.meta?.customer_id) nav(`/dashboard/customers/${n.meta.customer_id}`);
  };

  const sendTest = async () => {
    try {
      await api.post("/notifications/test");
      toast.success("Test notification queued");
      await refresh();
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const pageCount = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-5" data-testid="notifications-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Notifications</h1>
          <p className="text-sm text-slate-500 mt-1">
            {total} total · <span className="font-bold text-blue-600">{unread}</span> unread
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={sendTest} data-testid="notif-send-test">
            <Sparkles className="w-4 h-4 mr-1.5" /> Send test
          </Button>
          <Button
            onClick={markAll}
            disabled={unread === 0}
            data-testid="notif-mark-all-page"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <CheckCheck className="w-4 h-4 mr-1.5" /> Mark all read
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="notif-search"
            value={qLive}
            onChange={(e) => setQLive(e.target.value)}
            placeholder="Search by customer, title or message…"
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={type} onValueChange={(v) => { setPage(0); setType(v); }}>
            <SelectTrigger className="w-[170px]" data-testid="notif-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(TYPE_LABELS).map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={readFilter} onValueChange={(v) => { setPage(0); setReadFilter(v); }}>
            <SelectTrigger className="w-[140px]" data-testid="notif-read-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unread">Unread</SelectItem>
              <SelectItem value="read">Read</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading && rows.length === 0 && (
          <div className="p-10 text-center text-xs text-slate-500">Loading…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-500">
            <Bell className="w-6 h-6 mx-auto mb-2 text-slate-300" />
            No notifications match your filters.
          </div>
        )}
        <ul className="divide-y divide-slate-100">
          {rows.map((n) => {
            const Icon = typeIcon(n.type);
            const tone = typeTone(n.type);
            return (
              <li key={n.id} data-testid={`notif-row-${n.id}`} className={`flex items-start gap-3 p-4 hover:bg-slate-50 transition-colors ${!n.is_read ? "bg-blue-50/40" : ""}`}>
                <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0" style={{ background: tone.bg, color: tone.fg }}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-slate-900">{n.title}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{TYPE_LABELS[n.type] || n.type}</span>
                    {!n.is_read && <span className="status-pill status-draft text-[10px]">unread</span>}
                  </div>
                  <div className="text-sm text-slate-700 mt-0.5">{n.message}</div>
                  <div className="text-[11px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!n.is_read && (
                    <Button size="sm" variant="outline" data-testid={`notif-mark-${n.id}`} onClick={() => markOne(n.id)}>
                      <Check className="w-3.5 h-3.5 mr-1" /> Read
                    </Button>
                  )}
                  {n.quote_id && (
                    <Button size="sm" data-testid={`notif-view-${n.id}`} onClick={() => open(n)} className="bg-blue-600 hover:bg-blue-700 text-white">
                      View quote
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex items-center justify-end gap-2 text-xs">
        <span className="text-slate-500">Page {page + 1} / {pageCount}</span>
        <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} data-testid="notif-prev">
          <ChevronLeft className="w-3.5 h-3.5" />
        </Button>
        <Button variant="outline" size="sm" onClick={() => setPage((p) => (p + 1 < pageCount ? p + 1 : p))} disabled={page + 1 >= pageCount} data-testid="notif-next">
          <ChevronRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
